import { ReactNode } from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { fmtCalendarDate, fmtDate } from '../../shared/Panel';
import { shortDateTime } from '../../shared/dateTime';
import { plural } from '../../shared/plural';
import { isExpired } from './planningStatus';
import { acceptanceStatusAppearance, STATUS_IN_TMS } from './acceptanceStatus';
import { DEMO_SOURCE_HINT, isDemo, sourceLabel, SOURCE_TMS } from './recordSource';
import { TMS_ORDER_STATUS_LABELS, tmsOrderStatusText } from './tmsOrderStatus';
import {
  ForkliftCell,
  LooseRecord,
  PlanningStatusChip,
  Vehicle,
} from './cells';
import { forkliftInfoOf, forkliftSummary } from './forklift';
import {
  loadingAlertHint,
  loadingAlertOf,
  lostVehicleAtOf,
  lostVehicleHint,
  pendingAlertsOf,
  REPLAN_ALERT_HINT,
  REPLAN_IMPORTANT_ALERT_HINT,
  SALE_RECOMMENDATION_LABEL,
  saleRecommendationOf,
  tmsMarkerTitle,
  VEHICLE_CHANGE_ALERT_HINT,
} from './markers';
import { vatHint, vatSuggestionOf } from './vatSuggestion';
import { useFeature } from '../featureFlags/FeatureGate';
import { FEATURE_EXCHANGE_COLUMN, FEATURE_SALE_RECOMMENDATION } from './listFeatures';
import { TradeTransferCaption } from './tradeTransferInfo';
import { exchangeStates } from './publicationStatus';
import { ExchangeChipsLegacy } from './legacy/ExchangeChips';
import { FEATURE_BANK_COLUMNS, variantsShownInVehicleCell } from './bankFtl';
import { ExecutionHintChip, executionHintText, EXECUTION_HINT_FIELD } from './processLead';
import { PreOrderStageChip, preOrderStageOf } from './preOrderStage';
import { markersHint, variantMarkersOf } from './variantValidity';

/**
 * Zwarte komórki listy banku zleceń.
 *
 * Lista miała 14 kolumn i przy 1366 px strona rozjeżdżała się do ~1900 px:
 * operator przewijał w prawo, żeby zobaczyć spółkę i źródło. Tu te same
 * informacje siedzą gęściej — **przeniesione, nie usunięte**:
 * - spółka jako kod kraju (pełna nazwa w dymku),
 * - „w stanie od” pod chipem stanu planowania,
 * - status zlecenia w TMS (W/P/C/Z) razem z osią zapisu do TMS,
 * - źródło rekordu przy numerze, i tylko gdy to nie jest TMS klienta
 *   (rekord z TMS to norma — chip przy każdym wierszu byłby szumem),
 * - alerty czekające na potwierdzenie, warianty, wózek i termin ważności
 *   w jednej kolumnie znaczników, pokazywanych tylko wtedy, gdy są.
 *
 * Giełda była tu znacznikiem do 07.10.2026; od prezentacji 02.10 (pkt 9) ma
 * własną kolumnę z „od kiedy” (`ExchangeCell` w `cells.tsx`).
 *
 * Długie teksty (relacja, kontrahent) są ucinane wielokropkiem, a pełna
 * treść jest w dymku — wiersz ma 1–2 linie niezależnie od nazwy.
 */

const asRecord = (value: unknown): LooseRecord | undefined =>
  value && typeof value === 'object' ? (value as LooseRecord) : undefined;

const asText = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

/**
 * Szerokość długich tekstów: ciasno do 1536 px (1366 z otwartym menu ma się
 * mieścić bez przewijania), szerzej na dużym ekranie, gdzie miejsce jest.
 * Węziej od 08.10.2026 (relacja 170 → 160, kontrahent 170 → 150): jedenaście
 * kolumn z „Giełdą” ma się zmieścić przy 1366 px (decyzja użytkownika);
 * pełna treść jest w dymku.
 */
export type MaxWidth = { xs: number; xl: number };
const ROUTE_WIDTH: MaxWidth = { xs: 160, xl: 240 };
const CONTRACTOR_WIDTH: MaxWidth = { xs: 150, xl: 300 };
/**
 * Węższe relacja i kontrahent przy osobnych kolumnach „Stawka” i „Typ
 * pojazdu” (S-24.10-13): dwie kolumny więcej, a pod relacją i kontrahentem
 * nie ma już drugiej linii — lista ma się mieścić przy 1366 px.
 */
export const ROUTE_WIDTH_WITH_RATE_TYPE: MaxWidth = { xs: 116, xl: 240 };
export const CONTRACTOR_WIDTH_WITH_RATE_TYPE: MaxWidth = { xs: 100, xl: 300 };
/**
 * Szerokości z produkcji 0.31.3 — bez kolumny „Giełda” (przełącznik
 * `exchangeColumn` wyłączony, wersja „obecna”; decyzja użytkownika 12.10.2026).
 */
const ROUTE_WIDTH_LEGACY: MaxWidth = { xs: 170, xl: 240 };
const CONTRACTOR_WIDTH_LEGACY: MaxWidth = { xs: 170, xl: 300 };

/** Jedna linia z wielokropkiem; pełny tekst w dymku. */
const Clamp = ({ text, maxWidth }: { text: string; maxWidth: MaxWidth }) => (
  <Tooltip title={text} enterDelay={400}>
    <Typography variant="body2" noWrap component="div" sx={{ maxWidth }}>
      {text}
    </Typography>
  </Tooltip>
);

const Dash = () => (
  <Typography variant="body2" color="text.secondary">
    —
  </Typography>
);

/**
 * Krótki kod spółki: kraj z kartoteki (PL / DE). Bez kraju — pierwsze słowo
 * nazwy, bo kodu nie zgadujemy z nazwy („GmbH” ≠ pewność, że to DE).
 */
export function companyCode(company: LooseRecord | undefined): string | undefined {
  const country = asText(company?.country);
  if (country) return country.toUpperCase();
  const name = asText(company?.name);
  return name ? name.split(/\s+/)[0] : undefined;
}

/** Spółka jako kod; pełna nazwa w dymku. */
export const CompanyCodeCell = ({ record }: { record: LooseRecord }) => {
  const company = asRecord(record.company);
  const code = companyCode(company);
  if (!code) return <Dash />;
  return (
    <Tooltip title={asText(company?.name) ?? code}>
      <Chip size="small" variant="outlined" label={code} />
    </Tooltip>
  );
};

/**
 * Chip źródła — tylko gdy rekord NIE pochodzi z TMS klienta. Dane demo są
 * czerwone (wymóg z 08.09.2026: „dane testowe muszą być oznaczone”).
 * Brak źródła to brak chipa: nic nie twierdzimy.
 */
export const SourceChip = ({ source }: { source: unknown }) => {
  const s = asText(source);
  if (!s || s === SOURCE_TMS) return null;
  if (isDemo(s)) {
    return (
      <Tooltip title={DEMO_SOURCE_HINT}>
        <Chip size="small" color="error" variant="outlined" label="DEMO" />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`Źródło: ${sourceLabel(s)}`}>
      <Chip size="small" variant="outlined" label={sourceLabel(s)} />
    </Tooltip>
  );
};

/** Numer zlecenia, a pod nim źródło, gdy nie jest to TMS klienta. */
export const NumberWithSourceCell = ({ record }: { record: LooseRecord }) => (
  <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
    <Typography variant="body2" noWrap component="div">
      {asText(record.externalId) ?? `#${String(record.id)}`}
    </Typography>
    <SourceChip source={record.sourceSystem} />
  </Stack>
);

/** Relacja w dwóch liniach: skąd, dokąd. Brak końca oznaczony „?”. */
export const CompactRouteCell = ({ record, maxWidth }: { record: LooseRecord; maxWidth?: MaxWidth }) => {
  const exchangeColumn = useFeature(FEATURE_EXCHANGE_COLUMN);
  const routeWidth = maxWidth ?? (exchangeColumn ? ROUTE_WIDTH : ROUTE_WIDTH_LEGACY);
  const origin = [record.fromCountry, record.fromCity].filter(Boolean).join(' ');
  const destination = [record.toCountry, record.toCity].filter(Boolean).join(' ');
  if (!origin && !destination) return <Dash />;
  return (
    <Tooltip title={`${origin || '?'} → ${destination || '?'}`} enterDelay={400}>
      <Box sx={{ maxWidth: routeWidth }}>
        <Typography variant="body2" noWrap component="div">
          {origin || '?'}
        </Typography>
        <Typography variant="body2" noWrap component="div">
          {`→ ${destination || '?'}`}
        </Typography>
      </Box>
    </Tooltip>
  );
};

/** „od 3 dni” — krótko, pełna data w dymku. */
export function sinceText(iso: string, now = new Date()): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `od ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `od ${hours} godz.`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'od 1 dnia' : `od ${days} dni`;
}

/**
 * Dymek pod chipem stanu: od kiedy, kto ustawił i — przy bezczynnym
 * zleceniu — dlaczego jest wyróżnione. Pełne zdania zamiast „W stanie od
 * … — jan · bez działania dłużej niż próg dla tego stanu” (poprawa
 * brzmienia 01.10.2026). „Użytkownik”, bo z loginu nie znamy rodzaju
 * gramatycznego („ustawił” / „ustawiła”).
 */
export function statusSinceHint(since: string, by: string | undefined, idle: boolean): string {
  return [
    `W tym stanie od ${fmtDate(since)}.`,
    by ? `Stan ustawił użytkownik ${by}.` : null,
    idle ? 'Zlecenie jest bezczynne — nikt się nim nie zajął w czasie wyznaczonym dla tego stanu.' : null,
  ]
    .filter((s): s is string => s !== null)
    .join(' ');
}

/**
 * Stan planowania, a pod nim od kiedy i kto — dawne kolumny „Stan planowania”
 * i „W stanie od” w jednej komórce. Bezczynność (flaga z API) koloruje
 * podpis i dokłada ikonę, jak wcześniej.
 */
export const PlanningCell = ({ record }: { record: LooseRecord }) => {
  const since = asText(record.planningStatusSince);
  const by = asText(asRecord(record.planningStatusBy)?.username);
  const idle = record.idle === true;
  return (
    <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
      <PlanningStatusChip record={record} />
      {since ? (
        <Tooltip title={statusSinceHint(since, by, idle)}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', maxWidth: 200 }}>
            <Typography
              variant="caption"
              noWrap
              color={idle ? 'warning.main' : 'text.secondary'}
            >
              {sinceText(since)}
              {by ? ` · ${by}` : ''}
            </Typography>
            {idle ? <WarningAmberIcon sx={{ fontSize: 14 }} color="warning" /> : null}
          </Stack>
        </Tooltip>
      ) : null}
      {/* Powód przekazania do Trade / zwrotu z listy (A6, 20.10.2026). */}
      <TradeTransferCaption record={record} />
    </Stack>
  );
};

/**
 * TMS: status zlecenia w TMS klienta (W/P/C/Z) i oś zapisu do TMS.
 *
 * Oś zapisu („Nowe”, „Do zapisu w TMS”, „Błąd zapisu”…) pokazujemy zawsze,
 * gdy nie jest to „W TMS” — wtedy jest informacją. „W TMS” przy znanym
 * statusie z TMS mówiłby to samo co litera, więc zostaje sama litera.
 */
export const TmsCell = ({ record }: { record: LooseRecord }) => {
  const code = asText(record.tmsOrderStatus);
  const acceptance = acceptanceStatusAppearance(record.acceptanceStatus);
  const showAcceptance = !code || record.acceptanceStatus !== STATUS_IN_TMS;
  const chips = (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      {code ? (
        <Tooltip title={`Status w TMS: ${tmsOrderStatusText(code)}`}>
          <Chip
            size="small"
            variant="outlined"
            label={code}
            color={TMS_ORDER_STATUS_LABELS[code] ? 'primary' : 'default'}
            sx={{ minWidth: 28 }}
          />
        </Tooltip>
      ) : null}
      {showAcceptance ? (
        <Chip
          size="small"
          label={acceptance.label}
          sx={{ color: acceptance.textColor, backgroundColor: acceptance.backgroundColor }}
        />
      ) : null}
    </Stack>
  );
  // Kto realizuje wg TMS (odpowiedzi Marathonu/Euvic 16.10.2026, pyt. 4a
  // i 30; przełącznik `executionDepartmentHint` — bez niego API pola nie
  // niesie). Pod statusem, samą nazwą działu: kolumna „TMS” mówi resztę,
  // a lista ma się mieścić przy 1366 px.
  // Etap prezlecenia (makieta back office v2, 20.10.2026; przełącznik
  // `preOrderStageInBank` — bez niego API pola nie niesie): szkic,
  // zatwierdzone, plik, „Jest w TMS” z dniem, szczegóły w dymku.
  const hint = executionHintText(record[EXECUTION_HINT_FIELD]);
  const stage = preOrderStageOf(record);
  if (!hint && !stage) return chips;
  return (
    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
      {chips}
      {stage ? <PreOrderStageChip record={record} /> : null}
      {hint ? <ExecutionHintChip record={record} short /> : null}
    </Stack>
  );
};

/** Kontrahent jedną linią; blokada widoczna ikoną jak wcześniej. */
export const CompactContractorCell = ({ record, maxWidth }: { record: LooseRecord; maxWidth?: MaxWidth }) => {
  const exchangeColumn = useFeature(FEATURE_EXCHANGE_COLUMN);
  const contractorWidth = maxWidth ?? (exchangeColumn ? CONTRACTOR_WIDTH : CONTRACTOR_WIDTH_LEGACY);
  const k = asRecord(record.contractor);
  if (!k) return <Dash />;
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <Clamp text={asText(k.legalName) ?? '—'} maxWidth={contractorWidth} />
      {k.blacklisted ? (
        <Tooltip title="Kontrahent zablokowany">
          <BlockIcon fontSize="small" color="error" />
        </Tooltip>
      ) : null}
    </Stack>
  );
};

/**
 * Ikona alertu z dymkiem. `role="img"`: dymek staje się nazwą ikony, więc
 * czytnik ekranu ją przeczyta — sama ikona MUI jest ukryta przed czytnikiem.
 */
const AlertMarker = ({ title, icon }: { title: string; icon: ReactNode }) => (
  <Tooltip title={title}>
    <Box component="span" role="img" sx={{ display: 'inline-flex' }}>
      {icon}
    </Box>
  </Tooltip>
);

/**
 * Ikony alertów jak przed wyróżnieniem ważnych (produkcja, przełącznik
 * `importantAlerts` wyłączony — 12.10.2026): przeplanowanie pomarańczowe
 * pierwsze, zmiana w TMS pomarańczowa (anulacja czerwona). Zmiana pojazdu
 * w TMS przychodzi tylko z własnym przełącznikiem — wtedy pomarańczowa za
 * zmianą w TMS.
 */
function legacyAlertMarkers(alerts: ReturnType<typeof pendingAlertsOf>): ReactNode[] {
  const markers: ReactNode[] = [];
  if (alerts.replan > 0) {
    markers.push(
      <AlertMarker
        key="replan"
        title={REPLAN_ALERT_HINT}
        icon={<AltRouteIcon fontSize="small" color="warning" />}
      />,
    );
  }
  if (alerts.change + alerts.cancellation > 0) {
    // Podejrzenie anulacji jest pilniejsze — czerwone, jak baner na karcie.
    const cancellation = alerts.cancellation > 0;
    const title = tmsMarkerTitle(alerts);
    markers.push(
      <AlertMarker
        key="tms"
        title={title}
        icon={<SyncProblemIcon fontSize="small" color={cancellation ? 'error' : 'warning'} />}
      />,
    );
  }
  if (alerts.vehicleChange > 0) {
    markers.push(
      <AlertMarker
        key="vehicleChange"
        title={VEHICLE_CHANGE_ALERT_HINT}
        icon={<LocalShippingOutlinedIcon fontSize="small" color="warning" />}
      />,
    );
  }
  return markers;
}

/**
 * Ikony alertów z wyróżnieniem ważnych (12.10.2026, `importantAlerts`):
 * zmiany w TMS i zmiana pojazdu czerwone, przeplanowanie z pewnym powodem
 * czerwone, z szacunku szare — ważne pierwsze.
 */
function importantAlertMarkers(alerts: ReturnType<typeof pendingAlertsOf>): ReactNode[] {
  const markers: ReactNode[] = [];
  if (alerts.change + alerts.cancellation > 0) {
    // Każda zmiana w TMS jest ważna — czerwona, jak karta zlecenia.
    const title = tmsMarkerTitle(alerts);
    markers.push(
      <AlertMarker
        key="tms"
        title={title}
        icon={<SyncProblemIcon fontSize="small" color="error" />}
      />,
    );
  }
  if (alerts.vehicleChange > 0) {
    markers.push(
      <AlertMarker
        key="vehicleChange"
        title={VEHICLE_CHANGE_ALERT_HINT}
        icon={<LocalShippingOutlinedIcon fontSize="small" color="error" />}
      />,
    );
  }
  if (alerts.replanImportant > 0) {
    markers.push(
      <AlertMarker
        key="replanImportant"
        title={REPLAN_IMPORTANT_ALERT_HINT}
        icon={<AltRouteIcon fontSize="small" color="error" />}
      />,
    );
  } else if (alerts.replan > 0) {
    // Same podpowiedzi z szacunku — stonowane, szare.
    markers.push(
      <AlertMarker
        key="replan"
        title={REPLAN_ALERT_HINT}
        icon={<AltRouteIcon fontSize="small" color="action" />}
      />,
    );
  }
  return markers;
}

/**
 * Znaczniki wiersza: alerty czekające na potwierdzenie, warianty, wózek,
 * termin ważności — każdy tylko wtedy, gdy jest. Brak wszystkich to
 * myślnik („nic tu nie wisi”). Giełda ma od 07.10.2026 własną kolumnę.
 *
 * Alerty idą pierwsze (omówienie 01.10.2026): to jedyne znaczniki, które
 * czekają na człowieka. Ikona tylko informuje — potwierdza się na karcie
 * zlecenia, a kliknięcie w wiersz ją otwiera.
 *
 * Przed nimi czerwona ikona czerwonej listy: zlecenie straciło pojazd
 * i czeka na ponowne obsadzenie (`lostVehicleAt` z API, reguła
 * `apps/api/src/user-offers/vehicle-loss.ts`).
 *
 * Odpowiedzi Marathonu 07.10.2026: ikona „jutro załadunek, bez auta”
 * (`loadingAlert`, przegląd o 15:00 dzień przed załadunkiem) obok czerwonej
 * listy, a chip „Do sprzedaży” (`saleRecommendation`, zlecenia STANDARD
 * i MEGA) za alertami — to rekomendacja, nie coś, co czeka na potwierdzenie.
 *
 * Decyzja użytkownika 12.10.2026 („B4 — wyróżnij te ważniejsze alerty”):
 * czerwone są tylko ważne alerty (reguła API `alert-importance.ts`) i stoją
 * pierwsze — czerwona lista, jutrzejszy załadunek bez auta, zmiany w TMS
 * (także zmiana pojazdu) i przeplanowanie z pewnym powodem. Przeplanowanie
 * z szacunku zostaje szarą ikoną za nimi. Za przełącznikiem
 * `importantAlerts` — wyłączony: ikony jak na produkcji
 * (`legacyAlertMarkers`).
 */
export const FlagsCell = ({ record }: { record: LooseRecord }) => {
  // Przełączniki (decyzja użytkownika 12.10.2026): bez nich — znaczniki jak
  // na produkcji 0.31.3 (bez „Do sprzedaży”, giełda jako znacznik).
  const saleOn = useFeature(FEATURE_SALE_RECOMMENDATION);
  const exchangeColumn = useFeature(FEATURE_EXCHANGE_COLUMN);
  const flags: ReactNode[] = [];
  const importantOn = useFeature('importantAlerts');
  // Makieta FTL (15.10.2026, `bankFtlColumns`): warianty zlecenia bez auta
  // stoją w „Pojeździe” z autorami — tu ich liczba byłaby drugi raz.
  const variantsInVehicle = useFeature(FEATURE_BANK_COLUMNS) && variantsShownInVehicleCell(record);

  const loading = loadingAlertOf(record);
  if (loading) {
    flags.push(
      <AlertMarker
        key="loadingAlert"
        title={loadingAlertHint(loading)}
        // Ważny alert (12.10.2026) — czerwony także dzień przed załadunkiem;
        // bez wyróżnienia ważnych — czerwony dopiero w dniu załadunku.
        icon={
          <EventBusyIcon
            fontSize="small"
            color={importantOn || loading.when === 'today' ? 'error' : 'warning'}
          />
        }
      />,
    );
  }

  const lostAt = lostVehicleAtOf(record);
  if (lostAt) {
    flags.push(
      <AlertMarker
        key="lostVehicle"
        title={lostVehicleHint(shortDateTime(lostAt))}
        icon={<LinkOffIcon fontSize="small" color="error" />}
      />,
    );
  }

  const alerts = pendingAlertsOf(record);
  flags.push(...(importantOn ? importantAlertMarkers(alerts) : legacyAlertMarkers(alerts)));

  // Warianty po terminie i w konflikcie w czasie (makieta back office v2,
  // 20.10.2026; API niesie `variantMarkers` tylko z `variantValidity`) —
  // ostrzeżenia, nie alerty do potwierdzenia: stoją za alertami. Krótkie
  // podpisy jak w makiecie („konflikt”), pełne zdanie w dymku — lista ma się
  // mieścić przy 1366 px (decyzja 08.10.2026; e2e: „wariant po terminie”
  // poszerzał „Znaczniki” o 13 px ponad okno).
  const variantMarkers = variantMarkersOf(record);
  if (variantMarkers) {
    const hint = markersHint(variantMarkers);
    if (hint.conflict) {
      flags.push(
        <Tooltip key="variantConflict" title={hint.conflict}>
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label="konflikt"
            data-testid="marker-variant-conflict"
          />
        </Tooltip>,
      );
    }
    if (hint.expired) {
      flags.push(
        <Tooltip key="variantExpired" title={hint.expired}>
          <Chip
            size="small"
            variant="outlined"
            color="error"
            label="po terminie"
            data-testid="marker-variant-expired"
          />
        </Tooltip>,
      );
    }
  }

  const sale = saleOn ? saleRecommendationOf(record) : null;
  if (sale) {
    flags.push(
      <Tooltip key="forSale" title={sale.text}>
        <Chip
          size="small"
          variant="outlined"
          color="info"
          icon={<SellOutlinedIcon />}
          label={SALE_RECOMMENDATION_LABEL}
        />
      </Tooltip>,
    );
  }

  // Poprawa brzmienia 01.10.2026: „2 warianty” zamiast skrótu „war. 2”,
  // a w dymku sens wariantu zamiast żargonu „żywe kandydatury”.
  const counts = asRecord(record._count);
  const variants = typeof counts?.candidates === 'number' ? counts.candidates : 0;
  if (variants > 0 && !variantsInVehicle) {
    flags.push(
      <Tooltip
        key="variants"
        title={`Do zlecenia wstępnie wskazano ${plural(variants, 'pojazd', 'pojazdy', 'pojazdów')} (aktywne warianty).`}
      >
        <Chip
          size="small"
          variant="outlined"
          label={plural(variants, 'wariant', 'warianty', 'wariantów')}
        />
      </Tooltip>,
    );
  }

  // Bez kolumny „Giełda” (przełącznik wyłączony) stan giełdowy stoi tu, jak
  // na produkcji 0.31.3 — za wariantami, przed wózkiem.
  if (!exchangeColumn && exchangeStates(record).length > 0) {
    flags.push(<ExchangeChipsLegacy key="exchange" record={record} />);
  }

  if (forkliftSummary(forkliftInfoOf(record))) {
    flags.push(<ForkliftCell key="forklift" record={record} />);
  }

  const deadline = asText(record.validUntil);
  if (deadline) {
    const expired = isExpired(deadline);
    flags.push(
      <Tooltip
        key="validUntil"
        title={
          expired
            ? `Ważne do ${fmtCalendarDate(deadline)}. Termin minął — zlecenie wymaga decyzji.`
            : `Ważne do ${fmtCalendarDate(deadline)}`
        }
      >
        <Chip
          size="small"
          variant="outlined"
          color={expired ? 'error' : 'default'}
          icon={expired ? <WarningAmberIcon /> : undefined}
          label={`do ${fmtCalendarDate(deadline)}`}
        />
      </Tooltip>,
    );
  }

  if (flags.length === 0) return <Dash />;
  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, maxWidth: 220, alignItems: 'center' }}>
      {flags}
    </Stack>
  );
};

/**
 * Pojazd jak w `cells.tsx`, ale węziej: przy zleceniu przewoźnika numer
 * stoi pod chipem, zamiast rozpychać kolumnę na szerokość obu.
 *
 * Do 19.10.2026 robiło to zawijanie wiersza (`flexWrap`), więc numer
 * schodził pod chip dopiero przy ściśniętej tabeli, a numer bez spacji
 * (np. „WGM1234A/WGM5678B”) i tak trzymał kolumnę na 150 px. Razem
 * z „Obsadzone przewoźnikiem” przewijało to listę tradera przy 1366 px
 * o 35 px (smoke stagingu 19.10.2026; lokalnie: najwęższa możliwa tabela
 * 1253 → 1123 px). Teraz numer zawsze pod chipem i łamany (`stacked`).
 */
export const CompactVehicleCell = ({ record }: { record: LooseRecord }) => (
  <Box sx={{ maxWidth: 150 }}>
    <Vehicle record={record} stacked />
  </Box>
);

/**
 * Rekomendowana stawka VAT — osobna kolumna listy (omówienie 01.10.2026:
 * „VAT ma być osobną kolumną na liście zleceń”, nie znacznikiem). Ta sama
 * reguła co sekcja na karcie; kolor jak tam: pewna — zielona, do
 * potwierdzenia — pomarańczowa. Brak rekomendacji to myślnik z powodem
 * w dymku — stawki nie zgadujemy.
 */
export const VatCell = ({ record }: { record: LooseRecord }) => {
  const vat = vatSuggestionOf(record);
  if (!vat) return <Dash />;
  const hint = vatHint(vat);
  if (vat.rate === null) {
    return (
      <Tooltip title={hint}>
        <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
          —
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={hint}>
      <Chip
        size="small"
        variant="outlined"
        color={vat.certain ? 'success' : 'warning'}
        label={vat.rate}
      />
    </Tooltip>
  );
};
